var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * Route wrapper for the vocabulary "Advanced edit" full-pane form (TASK-071).
 *
 * Hybrid UX: the modal {@link ../components/VocabularyForm} stays the default fast
 * entry point; this route is reached via the modal's "Rozšířená editace" button.
 *
 * Behaviour:
 *  - Edit mode (`/vocabularies/:id/edit`) — fetches the vocabulary and seeds the
 *    generic form state from `translations.cs.{name, code}`. PUT on save with
 *    `{ translations, version }`.
 *  - Create mode (`/vocabularies/new?type=…`) — empty form. POST on save with
 *    `{ type, translations: { cs: { name, code? } } }`.
 *  - Without `?type=` in create mode, redirects back to the list (the type must
 *    be chosen first; the modal flow handles that selection naturally).
 *
 * Mirrors {@link RecordEdit} structurally but uses {@link vocabularyFormConfig}
 * (a single-tab MARC 901 layout) and the vocabulary service for IO.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useCallout, useOkapiKy } from '@folio/stripes/core';
import { Loading, Layout, MessageBanner, } from '@folio/stripes/components';
import RecordForm from '../components/ProposalForm/RecordForm';
import { ELECTRONIC_SOURCE_FLAGGED, vocabularyFormConfigFor, } from '../components/ProposalForm/config/vocabularyFormConfig';
import { getDefaultGenericFormState } from '../components/ProposalForm/formTypes';
import { createVocabulary, fetchVocabulary, updateVocabulary, } from '../api/vocabularyService';
import { SOURCE_CITATION_VOCABULARY, VOCABULARY_TYPE_CONFIG, } from '../types/api';
import { formatSaveError } from '../utils/formatSaveError';
import { isTimeoutError } from '../utils/errorKind';
import { saveOutcomeUnknownError, verificationFromVersions, } from '../utils/saveOutcomeUnknown';
import RecordReviewActions from '../components/RecordReviewActions';
import useRecordDetail from '../hooks/useRecordDetail';
var VOCABULARIES_BASE = '/czna-authorities/vocabularies';
/**
 * Build the GenericFormState for the 901 field from a vocabulary record.
 * Reads `translations.cs` first, falling back to flat name/code for legacy
 * records (mirrors VocabularyForm's buildInitialRows). CZNA-1662: a source citation's
 * electronic-source flag is its `$e`.
 */
export function vocabularyToFormState(v) {
    var _a, _b, _c, _d, _e, _f;
    var cs = (_b = (_a = v.translations) === null || _a === void 0 ? void 0 : _a.cs) !== null && _b !== void 0 ? _b : { name: (_c = v.name) !== null && _c !== void 0 ? _c : '', code: (_d = v.code) !== null && _d !== void 0 ? _d : undefined };
    return {
        fields: {
            '901': [
                __assign({ a: { value: (_e = cs.name) !== null && _e !== void 0 ? _e : '' }, c: { value: (_f = cs.code) !== null && _f !== void 0 ? _f : '' } }, (v.type === SOURCE_CITATION_VOCABULARY
                    ? { e: { value: v.electronicSource ? ELECTRONIC_SOURCE_FLAGGED : '' } }
                    : {})),
            ],
        },
    };
}
/**
 * Map the GenericFormState back to a translations map keyed by `cs`, plus — for a source
 * citation (CZNA-1662) — its electronic-source flag; another vocabulary sends none, so its
 * stored state is left alone. The advanced pane is cs-only; en remains the modal's
 * responsibility. Empty `code` is dropped so we don't send `code: ""` to the server.
 */
export function formStateToVocabulary(state, type) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var inst = (_b = (_a = state.fields['901']) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : {};
    var name = (_e = (_d = (_c = inst.a) === null || _c === void 0 ? void 0 : _c.value) === null || _d === void 0 ? void 0 : _d.trim()) !== null && _e !== void 0 ? _e : '';
    var code = (_g = (_f = inst.c) === null || _f === void 0 ? void 0 : _f.value) === null || _g === void 0 ? void 0 : _g.trim();
    var translations = { cs: code ? { name: name, code: code } : { name: name } };
    return type === SOURCE_CITATION_VOCABULARY
        ? { translations: translations, electronicSource: ((_h = inst.e) === null || _h === void 0 ? void 0 : _h.value) === ELECTRONIC_SOURCE_FLAGGED }
        : { translations: translations };
}
var VocabularyEdit = function () {
    var _a, _b;
    var intl = useIntl();
    var history = useHistory();
    var location = useLocation();
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    // CZNA-1625 — the app-wide callout: the "saved after all" news is given as the form leaves.
    var callout = useCallout();
    var id = useParams().id;
    var isEdit = Boolean(id);
    // Review state belongs to the record endpoint, while the vocabulary endpoint owns
    // the editable translations. Read both so the edit form can expose the same workflow
    // actions as the detail without inventing review state on VocabularySummary.
    var reviewRecord = useRecordDetail(isEdit ? id : undefined).record;
    // Type comes from query param (?type=…) in create mode, from the loaded
    // vocabulary in edit mode.
    var queryType = useMemo(function () {
        return new URLSearchParams(location.search).get('type') || undefined;
    }, [location.search]);
    var _c = useState(null), vocabulary = _c[0], setVocabulary = _c[1];
    var _d = useState(isEdit), isLoading = _d[0], setIsLoading = _d[1];
    var _e = useState(null), loadError = _e[0], setLoadError = _e[1];
    // Create mode without a type cannot work — redirect back to the list and let
    // the user pick the type via the type filter / create modal.
    useEffect(function () {
        if (!isEdit && !queryType) {
            history.replace(VOCABULARIES_BASE);
        }
    }, [isEdit, queryType, history]);
    // Edit mode: load vocabulary by id.
    // `ky` is intentionally omitted from deps — `useOkapiKy()` returns a fresh
    // ky.create() per render, so including it would re-run this effect every
    // render and cause an infinite refetch loop.
    useEffect(function () {
        var cancelled = false;
        if (!isEdit || !id) {
            return function () { };
        }
        setIsLoading(true);
        fetchVocabulary(ky, id)
            .then(function (v) {
            if (!cancelled) {
                setVocabulary(v);
                setIsLoading(false);
            }
        })
            .catch(function () {
            if (!cancelled) {
                setLoadError('loadError');
                setIsLoading(false);
            }
        });
        return function () {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, id]);
    var handleCancel = function () {
        if (isEdit && id) {
            history.push("".concat(VOCABULARIES_BASE, "/").concat(id));
        }
        else {
            history.push(VOCABULARIES_BASE);
        }
    };
    /**
     * CZNA-1625 — the version the next save goes out on, once a timed-out save has been verified
     * as NOT written. Same reason as `RecordEdit`'s `adoptedLockRef` (CZNA-773): putting the
     * re-read vocabulary into state would change RecordForm's key and remount it, wiping the
     * edits. `from` pins it to the vocabulary it was read over.
     */
    var adoptedVersionRef = useRef(null);
    var currentVersion = function (v) { return (adoptedVersionRef.current
        && adoptedVersionRef.current.id === v.id
        && adoptedVersionRef.current.from === v.version
        ? adoptedVersionRef.current.version
        : v.version); };
    /**
     * CZNA-1625 — "Načíst znovu" under an edit the server did not answer in time: read the
     * vocabulary and compare its version with the one the save went out on. Moved → it landed:
     * announce it and open the detail, as a successful save would. Unchanged → nothing was
     * written: the edits stay, and the next save goes out on the version just read.
     */
    var verifyTimedOutSave = function (current, sentVersion) { return __awaiter(void 0, void 0, void 0, function () {
        var fresh, _a, outcome;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, fetchVocabulary(ky, current.id)];
                case 1:
                    fresh = _b.sent();
                    return [3 /*break*/, 3];
                case 2:
                    _a = _b.sent();
                    return [2 /*return*/, 'unverified'];
                case 3:
                    outcome = verificationFromVersions(sentVersion, fresh === null || fresh === void 0 ? void 0 : fresh.version);
                    if (outcome === 'saved') {
                        queryClient.invalidateQueries('czna-vocabularies');
                        callout === null || callout === void 0 ? void 0 : callout.sendCallout({
                            type: 'success',
                            message: intl.formatMessage({ id: 'ui-czna-authorities.saveTimeout.saved' }),
                        });
                        history.push("".concat(VOCABULARIES_BASE, "/").concat(current.id));
                    }
                    else if (outcome === 'notSaved') {
                        adoptedVersionRef.current = { id: current.id, from: current.version, version: fresh.version };
                    }
                    return [2 /*return*/, outcome];
            }
        });
    }); };
    var handleSave = function (formData) { return __awaiter(void 0, void 0, void 0, function () {
        var sentVersion, updated, created, error_1, edited_1, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    sentVersion = vocabulary ? currentVersion(vocabulary) : undefined;
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 6, , 8]);
                    if (!(isEdit && vocabulary)) return [3 /*break*/, 3];
                    return [4 /*yield*/, updateVocabulary(ky, vocabulary.id, __assign(__assign({}, formStateToVocabulary(formData, vocabulary.type)), { version: sentVersion }))];
                case 2:
                    updated = _b.sent();
                    queryClient.invalidateQueries('czna-vocabularies');
                    history.push("".concat(VOCABULARIES_BASE, "/").concat(updated.id));
                    return [3 /*break*/, 5];
                case 3: return [4 /*yield*/, createVocabulary(ky, __assign({ type: queryType }, formStateToVocabulary(formData, queryType)))];
                case 4:
                    created = _b.sent();
                    queryClient.invalidateQueries('czna-vocabularies');
                    history.push("".concat(VOCABULARIES_BASE, "/").concat(created.id));
                    _b.label = 5;
                case 5: return [3 /*break*/, 8];
                case 6:
                    error_1 = _b.sent();
                    // CZNA-1625: no answer in time — the write may have landed. An edit can be verified
                    // against the stored version; a create has no id to read, and says only to check.
                    if (isTimeoutError(error_1) && isEdit && vocabulary) {
                        edited_1 = vocabulary;
                        throw saveOutcomeUnknownError(intl.formatMessage({ id: 'ui-czna-authorities.record.edit.timeout' }), { kind: 'verify', verify: function () { return verifyTimedOutSave(edited_1, sentVersion); } });
                    }
                    _a = Error.bind;
                    return [4 /*yield*/, formatSaveError(error_1, intl)];
                case 7: throw new (_a.apply(Error, [void 0, _b.sent()]))();
                case 8: return [2 /*return*/];
            }
        });
    }); };
    if (loadError) {
        return (React.createElement(Layout, { className: "padding-all-gutter" },
            React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularies.detail.loadError" }))));
    }
    if (isLoading) {
        return React.createElement(Loading, null);
    }
    // Determine the resolved type for create-mode redirect race: if we are still
    // here on create without a type, render nothing (the redirect effect will fire).
    var resolvedType = isEdit ? vocabulary === null || vocabulary === void 0 ? void 0 : vocabulary.type : queryType;
    if (!resolvedType) {
        return null;
    }
    // Always provide a populated GenericFormState — RecordForm's MARC preview
    // pipeline reads `formData.fields` synchronously on mount and crashes if the
    // form has no registered defaults (an empty react-hook-form returns no
    // `fields` key). Mirrors ProposalForm.tsx, which uses the same helper.
    var formConfig = vocabularyFormConfigFor(resolvedType);
    var initialData = vocabulary
        ? vocabularyToFormState(vocabulary)
        : getDefaultGenericFormState(formConfig);
    var typeLabelKey = (_a = VOCABULARY_TYPE_CONFIG[resolvedType]) === null || _a === void 0 ? void 0 : _a.labelKey;
    var paneSub = typeLabelKey ? intl.formatMessage({ id: typeLabelKey }) : resolvedType;
    return (React.createElement(RecordForm, { key: "".concat(id !== null && id !== void 0 ? id : 'new', "-").concat((_b = vocabulary === null || vocabulary === void 0 ? void 0 : vocabulary.version) !== null && _b !== void 0 ? _b : 0), config: formConfig, 
        // Vocabulary types (e.g. VOCABULARY_NAME_FORM) are not part of the
        // RecordType enum but the prop only feeds the pane-subtitle default,
        // which we override below — the cast is safe.
        recordType: resolvedType, initialData: initialData, isEdit: isEdit, mode: isEdit ? 'update' : 'create', onSave: handleSave, onCancel: handleCancel, extraFooterActions: isEdit && reviewRecord ? function (_a) {
            var isDirty = _a.isDirty;
            return (React.createElement(RecordReviewActions, { record: reviewRecord, disabled: isDirty, onActionComplete: function () { return history.push("".concat(VOCABULARIES_BASE, "/").concat(reviewRecord.id)); } }));
        } : null, paneSub: paneSub, paneId: "vocabularyEditPane", 
        // Force the simple "Save" label in both create and edit — the default
        // for create mode is "Save as draft", which doesn't fit the vocabulary
        // (no draft workflow on this entity).
        saveButtonLabelKey: "ui-czna-authorities.proposalForm.save", 
        // Same reason, one dialog further in: the leave guard's default text
        // tells the cataloguer her "návrh" will not even reach Koncepty and to
        // save it as a draft instead. A vocabulary term has no drafts and no
        // Koncepty, so that advice cannot be followed here.
        leaveModalHeadingKey: "ui-czna-authorities.vocabularies.advancedEdit.leaveModal.heading", leaveModalMessageKey: "ui-czna-authorities.vocabularies.advancedEdit.leaveModal.message" }));
};
export default VocabularyEdit;
